import { useMutation, useQueryClient } from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { useSession } from 'next-auth/react';
import { ShopTypes } from '~/lib/data-contract';
import { deleteUser } from '../../operations/mit-fog';

export default function useDeleteUser() {
    const { data } = useSession();
    const token = data?.accessToken ?? null;
    const queryClient = useQueryClient();

    function mutationFn({ customerId, userId }: Variables): Promise<string> {
        if (!token) {
            return Promise.reject(new Error('Cannot delete user: session not ready'));
        }
        return deleteUser({
            customerId,
            userId,
            token,
        });
    }

    return useMutation<string, HTTPError | Error, Variables, Context>(mutationFn, {
        onMutate: async ({ customerId, userId }: Variables) => {
            if (!customerId || !userId) {
                return;
            }
            await queryClient.cancelQueries(['userList', { customerId }]);

            const previousUserList = queryClient.getQueryData<ShopTypes.CustomerUsersResponse>([
                'userList',
                { customerId },
            ]);

            if (previousUserList) {
                queryClient.setQueryData<ShopTypes.CustomerUsersResponse>(
                    ['userList', { customerId }],
                    (old) => {
                        if (old) {
                            return {
                                ...old,
                                users: old.users.filter((user) => user.userId !== userId),
                            };
                        }
                        return old;
                    },
                );
            }

            return { previousUserList };
        },

        onError: (_error, _, context) => {
            const userBeforeMutation = context?.previousUserList;
            if (userBeforeMutation) {
                queryClient.setQueryData(
                    ['userList', { customerId: userBeforeMutation.customerId }],
                    userBeforeMutation,
                );
            }
        },

        onSettled: (_error, _, variables) => {
            queryClient.invalidateQueries(['userList', { customerId: variables.customerId }]);
        },
    });
}

type Variables = Omit<Parameters<typeof deleteUser>[0], 'token'>;

type Context = {
    previousUserList: ShopTypes.CustomerUsersResponse | undefined;
};
